/**
 * FinancialActionPlanSection (FIN-2) — the deterministic financial action plan.
 *
 * READ-ONLY over the persisted plan. Prefers `financialAnalysis.actionPlan`
 * (always stamped) and falls back to `report.conclusion` — the SAME fields
 * `buildFinancialActionPlan` wrote (btmDecisionSummary headline + nextActions
 * with `owner: …; deadline: …` tokens that recommendationGraphBuilder /
 * actionDeduper extract).
 *
 * WHY THE STAMP COMES FIRST: a WITHHELD verdict writes no `conclusion` at all.
 * `conclusion.recommendation` is a 3-value enum with no "withheld" member, and
 * many surfaces read it raw, so stamping a fabricated 'Proceed with Conditions'
 * to carry the guidance published a Conditional verdict for a report that has
 * none. The guidance now lives on the stamp, where it needs no enum value — so
 * a withheld report can still tell the reader what to do next.
 *
 * This section renders the persisted actions as a themed owner/deadline
 * checklist; it does NOT re-author the plan. Themed via roleColor + the `hair`
 * hairline role.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge } from '../../report/editorial';
import { FIN_MONO, parseFinancialAction } from '../../../../lib/reportRendering/financialFormat';


export const FinancialActionPlanSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const conclusion = report.conclusion;
  const stampPlan = report.financialAnalysis?.actionPlan;
  const actions = (stampPlan?.actions ?? conclusion?.nextActions ?? [])
    .filter((a) => a && a.trim().length > 0);
  const headline = (stampPlan?.recommendation ?? conclusion?.btmDecisionSummary)?.trim();
  const hasData = actions.length > 0 || Boolean(headline);

  return (
    <SectionShell label={label} hasData={hasData}>
      {hasData ? (
        <div className="space-y-3">
          {headline ? (
            <p className="text-sm font-medium" style={{ color: roleColor(theme, 'ink') }}>
              {headline}
            </p>
          ) : null}
          {actions.length > 0 ? (
            <ol className="space-y-2" data-testid="financial-action-plan">
              {actions.map((raw, i) => {
                const a = parseFinancialAction(raw);
                return (
                  <li
                    key={`fap-${i}`}
                    className="flex flex-col gap-1 py-2"
                    style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}
                  >
                    <span className="text-sm flex gap-2" style={{ color: roleColor(theme, 'body') }}>
                      <span
                        className="shrink-0 font-bold tabular-nums"
                        style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'accent') }}
                      >
                        {i + 1}.
                      </span>
                      <span>{a.text}</span>
                    </span>
                    {(a.owner || a.deadline) ? (
                      <span className="flex flex-wrap gap-2 pl-5">
                        {a.owner ? (
                          <EditorialBadge theme={theme} tone="accent" variant="outline">
                            owner · {a.owner}
                          </EditorialBadge>
                        ) : null}
                        {a.deadline ? (
                          <EditorialBadge theme={theme} tone="neutral" variant="outline">
                            deadline · {a.deadline}
                          </EditorialBadge>
                        ) : null}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          ) : null}
        </div>
      ) : null}
    </SectionShell>
  );
};
